import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, RefreshCw, Trash2, Edit } from "lucide-react";
import { toast } from "sonner";
import { api, loadAuthToken } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";

const AdminBranchesPage = () => {
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!loadAuthToken()) { navigate("/admin/login"); return; }
    load();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/branches");
      setBranches(r.data || []);
    } catch (err) {
      if (err?.response?.status === 401) navigate("/admin/login");
      else toast.error(err?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const del = async (b) => {
    if (!window.confirm(`Delete branch ${b.name_en}?`)) return;
    try {
      await api.delete(`/admin/branches/${b.id}`);
      toast.success("Deleted"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-branches-page" className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-10">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="outline" size="sm" onClick={() => navigate("/admin")}><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-[0.14em] text-teal-700 font-semibold">Super Admin</div>
          <h1 className="mt-1 text-2xl sm:text-3xl font-heading font-semibold text-slate-900">Branch Management</h1>
        </div>
        <BranchDialog open={open} onOpenChange={setOpen} onDone={load} />
      </div>

      <div className="rounded-lg bg-white border border-slate-200 overflow-hidden">
        <div className="flex justify-end p-3 border-b border-slate-100">
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Code</th>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Address</th>
                <th className="px-4 py-3 font-medium">Mobile</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {branches.map((b) => (
                <tr key={b.id} className="border-t border-slate-100" data-testid={`branch-row-${b.id}`}>
                  <td className="px-4 py-3 font-medium">{b.code}</td>
                  <td className="px-4 py-3">{b.name_en}</td>
                  <td className="px-4 py-3 text-slate-600">{b.address_en}</td>
                  <td className="px-4 py-3">{b.mobile}</td>
                  <td className="px-4 py-3"><Badge className={b.active === false ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}>{b.active === false ? "Inactive" : "Active"}</Badge></td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="destructive" onClick={() => del(b)} data-testid={`delete-branch-${b.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </td>
                </tr>
              ))}
              {branches.length === 0 && !loading && <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-500">No branches.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const BranchDialog = ({ open, onOpenChange, onDone }) => {
  const [form, setForm] = useState({ code: "", name_en: "", name_hi: "", address_en: "", address_hi: "", mobile: "", whatsapp: "", map_url: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/branches", form);
      toast.success("Branch created");
      onDone(); onOpenChange(false);
      setForm({ code: "", name_en: "", name_hi: "", address_en: "", address_hi: "", mobile: "", whatsapp: "", map_url: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const ch = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="create-branch-btn"><Plus className="w-4 h-4 mr-2" /> New Branch</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Create Branch</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <div><Label>Code (e.g. DGH)</Label><Input value={form.code} onChange={ch("code")} data-testid="branch-code" /></div>
          <div><Label>Name (English)</Label><Input value={form.name_en} onChange={ch("name_en")} data-testid="branch-name-en" /></div>
          <div><Label>Name (Hindi)</Label><Input value={form.name_hi} onChange={ch("name_hi")} /></div>
          <div><Label>Address (English)</Label><Textarea rows={2} value={form.address_en} onChange={ch("address_en")} /></div>
          <div><Label>Address (Hindi)</Label><Textarea rows={2} value={form.address_hi} onChange={ch("address_hi")} /></div>
          <div><Label>Mobile</Label><Input value={form.mobile} onChange={ch("mobile")} data-testid="branch-mobile" /></div>
          <div><Label>WhatsApp (digits only)</Label><Input value={form.whatsapp} onChange={ch("whatsapp")} /></div>
          <div><Label>Google Maps URL</Label><Input value={form.map_url} onChange={ch("map_url")} /></div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="branch-submit">{saving ? "…" : "Create"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminBranchesPage;
